import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, BedDouble, Download, Eye, Loader2, Printer, Sparkles } from "lucide-react";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { RoomSheetPreview } from "../components/rooms/RoomSheetPreview";
import { downloadRoomSheetPdf, waitForSheetAssets } from "../components/rooms/roomSheetPdf";
import { toast } from "sonner";

export default function RoomSheetsPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const singleSheetRef = useRef(null);

  const requestedId = searchParams.get("id");
  const requestedType = searchParams.get("type");
  const action = searchParams.get("action");
  const printAll = searchParams.get("all") === "1";
  const selected = useMemo(
    () => sheets.find((sheet) => sheet.id === requestedId && sheet.kind === requestedType),
    [sheets, requestedId, requestedType],
  );

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { nav("/admin/login"); return; }
    api.get("/admin/logistics/door-sheets")
      .then(({ data }) => setSheets(data))
      .catch((error) => {
        if (error?.response?.status === 401 || error?.response?.status === 403) nav("/admin/login");
        else toast.error("No se pudieron cargar las hojas");
      })
      .finally(() => setLoading(false));
  }, [nav]);

  useEffect(() => {
    if (loading || !selected || !action) return undefined;
    const timer = setTimeout(async () => {
      try {
        if (action === "print") {
          await waitForSheetAssets(singleSheetRef.current);
          window.print();
        }
        if (action === "pdf") {
          setDownloading(true);
          await downloadRoomSheetPdf(singleSheetRef.current, selected);
          setDownloading(false);
        }
      } catch (error) {
        setDownloading(false);
        toast.error(error.message || "No se pudo preparar la hoja");
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [action, loading, selected]);

  useEffect(() => {
    if (loading || !printAll || action !== "print" || sheets.length === 0) return undefined;
    const timer = setTimeout(async () => {
      const root = document.querySelector(".room-sheet-print-root");
      await waitForSheetAssets(root);
      window.print();
    }, 700);
    return () => clearTimeout(timer);
  }, [action, loading, printAll, sheets.length]);

  const openSheet = (sheet, nextAction) => {
    const query = new URLSearchParams({ type: sheet.kind, id: sheet.id });
    if (nextAction) query.set("action", nextAction);
    const url = `/admin/logistica/hojas?${query.toString()}`;
    if (nextAction) window.open(url, "_blank", "noopener,noreferrer");
    else nav(url);
  };

  const downloadSelected = async () => {
    setDownloading(true);
    try {
      await downloadRoomSheetPdf(singleSheetRef.current, selected);
      toast.success("PDF descargado");
    } catch (error) {
      toast.error(error.message || "No se pudo descargar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <LoadingState />;

  if (selected || printAll) {
    const visibleSheets = printAll ? sheets : [selected];
    return (
      <div className="min-h-screen bg-neutral-900 py-6 sm:py-10 px-3" data-testid="room-sheet-viewer">
        <div className="no-print max-w-[816px] mx-auto mb-5 flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" onClick={() => nav("/admin/logistica/hojas")} data-testid="room-sheets-back-gallery" className="bg-transparent text-white border-white/30 hover:bg-white hover:text-black">
            <ArrowLeft className="w-4 h-4 mr-2" /> Todas las hojas
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => window.print()} data-testid="btn-print-sheet" className="bg-yellow-400 text-black hover:bg-yellow-300">
              <Printer className="w-4 h-4 mr-2" /> {printAll ? "Imprimir todas" : "Imprimir"}
            </Button>
            {!printAll && (
              <Button onClick={downloadSelected} disabled={downloading} data-testid="btn-download-pdf" className="bg-pink-600 text-white hover:bg-pink-500">
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Descargar PDF
              </Button>
            )}
          </div>
        </div>
        <main className="room-sheet-print-root space-y-8 print:space-y-0" data-testid="room-sheets-print-root">
          {visibleSheets.map((sheet, index) => (
            <RoomSheetPreview key={`${sheet.kind}-${sheet.id}`} room={sheet} sheetRef={!printAll && index === 0 ? singleSheetRef : undefined} />
          ))}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="room-sheets-gallery">
      <header className="border-b border-white/10 bg-black/90 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => nav("/admin/logistica?tab=rooms")} data-testid="room-sheets-back-logistics" className="text-white hover:text-yellow-400 hover:bg-white/5">
              <ArrowLeft className="w-4 h-4 mr-2" /> Logística
            </Button>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-400">Operación Rescate</div>
              <h1 className="font-heading text-2xl sm:text-3xl uppercase">Hojas de habitaciones</h1>
            </div>
          </div>
          <Button
            onClick={() => window.open("/admin/logistica/hojas?all=1&action=print", "_blank", "noopener,noreferrer")}
            disabled={sheets.length === 0}
            data-testid="room-sheets-print-all"
            className="bg-yellow-400 text-black hover:bg-yellow-300 font-bold"
          >
            <Printer className="w-4 h-4 mr-2" /> Imprimir todas ({sheets.length})
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <p className="text-white/55 max-w-2xl mb-7">Una misma plantilla profesional para cada habitación y cabaña VIP, usando sus nombres, colores y asignaciones actuales.</p>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {sheets.map((sheet) => (
            <article key={`${sheet.kind}-${sheet.id}`} className="relative overflow-hidden border border-white/10 bg-white/5 rounded-lg p-5" data-testid={`room-sheet-card-${sheet.kind}-${sheet.id}`}>
              <div className="absolute inset-x-0 top-0 h-1.5" style={{ backgroundColor: sheet.color || "#fbbf24" }} />
              <div className="flex items-start justify-between gap-3 mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 flex items-center gap-1.5">
                    {sheet.kind === "cabin" ? <Sparkles className="w-3 h-3" /> : <BedDouble className="w-3 h-3" />}{sheet.kind_label}
                  </div>
                  <h2 className="font-heading text-2xl uppercase mt-1" data-testid={`room-sheet-card-name-${sheet.id}`}>{sheet.name}</h2>
                </div>
                <span className="w-7 h-7 flex-shrink-0" style={{ backgroundColor: sheet.color || "#fbbf24" }} aria-label={`Color de ${sheet.name}`} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center mb-5">
                <Metric label="Capacidad" value={sheet.capacity} />
                <Metric label="Integrantes" value={sheet.members_count} />
                <Metric label="Líderes" value={sheet.leaders_count} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" onClick={() => openSheet(sheet)} data-testid={`room-sheet-view-${sheet.kind}-${sheet.id}`} className="border-white/20 bg-transparent text-white hover:bg-white hover:text-black">
                  <Eye className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Ver</span>
                </Button>
                <Button size="sm" onClick={() => openSheet(sheet, "print")} data-testid={`room-sheet-print-${sheet.kind}-${sheet.id}`} className="bg-yellow-400 text-black hover:bg-yellow-300">
                  <Printer className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Imprimir</span>
                </Button>
                <Button size="sm" onClick={() => openSheet(sheet, "pdf")} data-testid={`room-sheet-pdf-${sheet.kind}-${sheet.id}`} className="bg-pink-600 text-white hover:bg-pink-500">
                  <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">PDF</span>
                </Button>
              </div>
            </article>
          ))}
        </div>
        {sheets.length === 0 && <div className="py-24 text-center text-white/45">Todavía no hay habitaciones o cabañas creadas.</div>}
      </main>
    </div>
  );
}

const Metric = ({ label, value }) => (
  <div className="border border-white/10 bg-black/25 rounded-md py-2 px-1">
    <div className="font-heading text-xl">{value}</div>
    <div className="text-[9px] uppercase tracking-wider text-white/45">{label}</div>
  </div>
);

const LoadingState = () => (
  <div className="min-h-screen bg-neutral-950 text-yellow-400 flex items-center justify-center" data-testid="room-sheets-loading">
    <Loader2 className="w-8 h-8 animate-spin" />
  </div>
);